import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FiX,
  FiArrowLeft,
  FiCalendar,
  FiUser,
  FiTag,
  FiClock,
  FiEdit2,
  FiCheck,
  FiXCircle,
  FiFileText,
  FiCheckCircle,
  FiAlertCircle,
  FiEye,
  FiShare2,
  FiBookmark,
  FiTrash2,
  FiRefreshCw
} from "react-icons/fi";
import { MdOutlineArchive } from "react-icons/md";
import { approvalService, publishedService, statsService, revisionService } from "../../services/firestoreService";
import { db } from "../../firebase";
import { doc, onSnapshot, serverTimestamp } from "firebase/firestore";
import { useLanguage } from "../../contexts/LanguageContext";
import "../AdminPage.css";

function AdminContentReview() {
  const { getTranslations } = useLanguage();
  const t = getTranslations(true);
  const { approvalId } = useParams();
  const navigate = useNavigate();
  const [approval, setApproval] = useState(null);
  const [published, setPublished] = useState(null);
  const [revision, setRevision] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [articleStats, setArticleStats] = useState({ views: 0, shares: 0, bookmarks: 0 });
  const [activeTab, setActiveTab] = useState('submission');
  const [statsCache, setStatsCache] = useState({});
  const [statsLoading, setStatsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejectError, setRejectError] = useState("");
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);

  useEffect(() => {
    let unsubscribeApproval = null;
    let unsubscribePublished = null;

    const setupListeners = async () => {
      try {
        setLoading(true);

        // First try to find by contentId in approvals
        let data = await approvalService.getApprovalByContentId(approvalId);
        
        // If not found by contentId, try by document ID (fallback for old data)
        if (!data) {
          data = await approvalService.getApprovalById(approvalId);
        }
        
        if (data) {
          // Set up real-time listener for approvals
          const approvalDocRef = doc(db, 'approvals', data.id);
          unsubscribeApproval = onSnapshot(approvalDocRef, (snapshot) => {
            if (snapshot.exists()) {
              const approvalData = { id: snapshot.id, ...snapshot.data() };
              setApproval(approvalData);
            }
          });
          setApproval(data);
          
          // Parallelize independent read operations (revision and stats)
          const independentOperations = [];
          
          // Check for revisions
          if (data.contentId) {
            independentOperations.push(
              revisionService.getRevisionByContentId(data.contentId).then(revisionData => {
                setRevision(revisionData);
              })
            );
          }
          
          // If content is approved, fetch stats from published collection and set up listener there too
          if (data.status === "Approved" && data.contentId) {
            independentOperations.push(
              fetchStats(data.contentId)
            );
            const publishedDocRef = doc(db, 'published', data.contentId);
            unsubscribePublished = onSnapshot(publishedDocRef, (snapshot) => {
              if (snapshot.exists()) {
                const publishedData = { id: snapshot.id, ...snapshot.data() };
                // Use separate published state to avoid data inconsistency
                setPublished(publishedData);
              }
            });
          }
          
          // Execute independent operations in parallel
          if (independentOperations.length > 0) {
            await Promise.all(independentOperations);
          }
        } else {
          // If not in approvals, check published
          const publishedData = await publishedService.getPublishedByContentId(approvalId);
          if (publishedData) {
            const publishedDocRef = doc(db, 'published', publishedData.contentId);
            unsubscribePublished = onSnapshot(publishedDocRef, (snapshot) => {
              if (snapshot.exists()) {
                const data = { id: snapshot.id, ...snapshot.data() };
                setPublished(data);
              }
            });
            setPublished(publishedData);
            
            // Parallelize independent read operations (revision and stats)
            const independentOperations = [];
            
            if (publishedData.contentId) {
              independentOperations.push(
                revisionService.getRevisionByContentId(publishedData.contentId).then(revisionData => {
                  setRevision(revisionData);
                })
              );
            }
            
            independentOperations.push(fetchStats(publishedData.contentId));
            
            // Execute independent operations in parallel
            await Promise.all(independentOperations);
          } else {
            setError("Content not found");
          }
        }
      } catch (err) {
        setError("Failed to load content. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    setupListeners();

    // Cleanup listeners on unmount
    return () => {
      if (unsubscribeApproval) unsubscribeApproval();
      if (unsubscribePublished) unsubscribePublished();
    };
  }, [approvalId]);

  const fetchStats = async (contentId) => {
    try {
      // Check cache first (5 minute TTL)
      const cachedStats = statsCache[contentId];
      const now = Date.now();
      if (cachedStats && (now - cachedStats.timestamp) < 5 * 60 * 1000) {
        setArticleStats(cachedStats.data);
        return;
      }
      
      setStatsLoading(true);
      const stats = await statsService.getStats(contentId);
      setArticleStats(stats);
      
      // Update cache
      setStatsCache(prev => ({
        ...prev,
        [contentId]: { data: stats, timestamp: now }
      }));
    } catch (error) {
      setArticleStats({ views: 0, shares: 0, bookmarks: 0 });
    } finally {
      setStatsLoading(false);
    }
  };

  // Helper to get the current content data (approval or published)
  const getContentData = () => {
    return approval || published || { status: "", bodyHtml: "" };
  };

  const handleApprove = async () => {
    setShowApproveModal(true);
  };

  const confirmApprove = async () => {
    try {
      setActionLoading(true);
      // Use the approval document ID for status updates
      const contentData = getContentData();
      const approvalDocId = contentData.id;
      await approvalService.updateApprovalStatus(approvalDocId, "Approved", {
        approvedAt: new Date().toISOString(),
        approvedBy: "Admin"
      });
      setShowApproveModal(false);
      navigate("/admin-management?tab=content");
    } catch (error) {
      alert("Failed to approve content. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  const cancelApprove = () => {
    setShowApproveModal(false);
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteModalClose = () => {
    setShowDeleteModal(false);
  };

  const confirmDelete = async () => {
    if (!getContentData()) return;
    try {
      setIsDeleting(true);
      await approvalService.deleteApproval(getContentData().id);
      navigate("/admin-management?tab=content");
    } catch (error) {
      console.error('Error deleting content:', error);
      alert('Failed to delete content. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRestoreClick = () => {
    setShowRestoreModal(true);
  };

  const handleRestoreModalClose = () => {
    setShowRestoreModal(false);
  };

  const confirmRestore = async () => {
    if (!getContentData()) return;
    
    try {
      setIsRestoring(true);
      
      // Add back to published collection
      await publishedService.addToPublished({
        title: getContentData().title,
        subtitle: getContentData().subtitle,
        bodyHtml: getContentData().bodyHtml,
        socialTitle: getContentData().socialTitle,
        socialDescription: getContentData().socialDescription,
        socialImageUrl: getContentData().socialImageUrl,
        category: getContentData().category,
        tags: getContentData().tags || [],
        author: getContentData().author,
        authorEmail: getContentData().authorEmail,
        authorRole: getContentData().authorRole,
        authorId: getContentData().authorId,
        approvedAt: getContentData().approvedAt,
        approvedBy: getContentData().approvedBy,
        contentId: getContentData().contentId
      });
      
      // Update approval status back to Approved
      await approvalService.updateApprovalStatus(getContentData().id, "Approved", {
        restoredAt: serverTimestamp(),
        restoredBy: "Admin"
      });
      
      setShowRestoreModal(false);
      navigate("/admin-management?tab=content");
    } catch (error) {
      console.error('Error restoring content:', error);
      alert('Failed to restore content. Please try again.');
    } finally {
      setIsRestoring(false);
    }
  };

  const handleArchiveClick = () => {
    setShowArchiveModal(true);
  };

  const handleArchiveModalClose = () => {
    setShowArchiveModal(false);
  };

  const confirmArchive = async () => {
    if (!getContentData()) return;
    
    try {
      setIsArchiving(true);
      
      // Remove from published collection
      if (getContentData().contentId) {
        await publishedService.removeFromPublished(getContentData().contentId);
      }
      
      // Update approval status to Archived
      await approvalService.updateApprovalStatus(getContentData().id, "Archived", {
        archivedAt: serverTimestamp(),
        archivedBy: "Admin"
      });
      
      setShowArchiveModal(false);
      navigate("/admin-management?tab=content");
    } catch (error) {
      console.error('Error archiving content:', error);
      alert('Failed to archive content. Please try again.');
    } finally {
      setIsArchiving(false);
    }
  };

  const handleReject = async () => {
    setShowRejectModal(true);
  };

  const handleReasonChange = (e) => {
    // Remove special characters, only allow letters, numbers, and spaces
    const value = e.target.value.replace(/[^a-zA-Z0-9\s]/g, '').substring(0, 50);
    setRejectionReason(value);
    setRejectError("");
  };

  const confirmReject = async () => {
    if (!rejectionReason.trim()) {
      setRejectError("Please provide a reason for rejection.");
      return;
    }
    
    setRejectError("");
    
    try {
      setActionLoading(true);
      // Use the approval document ID for status updates
      const contentData = getContentData();
      const approvalDocId = contentData.id;
      await approvalService.updateApprovalStatus(approvalDocId, "Rejected", {
        rejectedAt: new Date().toISOString(),
        rejectedBy: "Admin",
        rejectionReason: rejectionReason
      });
      setShowRejectModal(false);
      setRejectionReason("");
      navigate("/admin-management?tab=content");
    } catch (error) {
      setRejectError("Failed to reject content. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  const cancelReject = () => {
    setShowRejectModal(false);
    setRejectionReason("");
    setRejectError("");
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return "N/A";
    
    // Handle Firestore timestamp objects
    let date;
    if (dateValue.toDate && typeof dateValue.toDate === 'function') {
      // Firestore timestamp object
      date = dateValue.toDate();
    } else if (dateValue instanceof Date) {
      // Already a Date object
      date = dateValue;
    } else {
      // Try to parse as string
      date = new Date(dateValue);
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return "Invalid Date";
    }
    
    const dateStr = date.toLocaleDateString();
    const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    
    return (
      <>
        {dateStr}<br />{timeStr}
      </>
    );
  };

  // Format date for modals (single line)
  const formatDateModal = (dateValue) => {
    if (!dateValue) return "N/A";
    
    let date;
    if (dateValue.toDate && typeof dateValue.toDate === 'function') {
      date = dateValue.toDate();
    } else if (dateValue instanceof Date) {
      date = dateValue;
    } else {
      date = new Date(dateValue);
    }
    
    if (isNaN(date.getTime())) {
      return "Invalid Date";
    }
    
    const dateStr = date.toLocaleDateString();
    const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    
    return `${dateStr} ${timeStr}`;
  };

  const stripHtmlToText = (html) => {
    try {
      const div = document.createElement("div");
      div.innerHTML = html || "";
      return (div.textContent || div.innerText || "").replace(/\s+/g, " ").trim();
    } catch {
      return "";
    }
  };

  const getStatusBadge = (status) => {
    const statusClass = status.toLowerCase();
    return (
      <span className={`status-badge status-badge-${statusClass}`}>
        {status}
      </span>
    );
  };

  if (error || (!loading && !approval && !published)) {
    return (
      <div className="content-review-page">
        <div className="error-state">
          <h3>Error</h3>
          <p>{error || "Content not found"}</p>
          <button className="back-btn" onClick={() => navigate("/admin-management?tab=content")}>
            <FiArrowLeft /> Back to Content Management
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <style>{`
        .content-review-header::before {
          display: none !important;
        }
        .sidebar-card::before,
        .sidebar-card::after {
          display: none !important;
        }
        .content-review-page .submission-meta {
          display: flex;
          gap: 12px;
          align-items: flex-start;
          flex-wrap: wrap;
          width: 100%;
        }
        .admin-aura-skeleton {
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
          border-radius: 4px;
        }
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .admin-aura-skeleton-text {
          height: 16px;
          margin-bottom: 8px;
        }
        .admin-aura-skeleton-title {
          height: 32px;
          width: 60%;
          margin-bottom: 16px;
        }
        .admin-aura-skeleton-image {
          width: 100%;
          height: 200px;
        }
      `}</style>
      <div className="content-review-page">
        <div className="content-review-header" style={{ 
          background: 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)',
          boxShadow: '0 4px 20px rgba(15, 23, 42, 0.3)',
          padding: '20px 32px',
          borderBottom: 'none',
          borderRadius: '0'
        }}>
          <div className="header-left">
            <button className="back-btn" 
              onClick={() => navigate("/admin-management?tab=content")}
              style={{ 
                background: 'rgba(255,255,255,0.12)', 
                color: '#fff', 
                borderRadius: '10px',
                border: '1px solid rgba(255,255,255,0.18)'
              }}
            >
              <FiArrowLeft />
            </button>
            <div className="header-title">
              <h1 style={{ color: '#fff' }}>Content Review</h1>
              <div className="submission-meta">
                <span className="submission-id-full" style={{ 
                  color: '#cbd5e1', 
                  maxWidth: '600px', 
                  wordBreak: 'break-word', 
                  whiteSpace: 'normal', 
                  lineHeight: '1.4',
                  fontSize: '14px', 
                  color: '#cbd5e1', 
                  fontWeight: '500', 
                  padding: '8px 16px', 
                  background: 'rgba(255, 255, 255, 0.15)', 
                  borderRadius: '12px', 
                  border: '1px solid rgba(255, 255, 255, 0.2)', 
                  backdropFilter: 'blur(10px)', 
                  WebkitBackdropFilter: 'blur(10px)', 
                  display: 'block', 
                  boxSizing: 'border-box' 
                }}>
                  {loading ? (
                    <div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '200px', height: '14px', background: 'rgba(255,255,255,0.2)' }} />
                  ) : (
                    `Content ID: ${approvalId || 'N/A'}`
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="content-review-body" style={{ 
          background: 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)',
          padding: '24px'
        }}>
          <div className="content-review-main">
            {/* Tabs Header */}
          <div style={{ 
            background: '#fff', 
            borderBottom: '1px solid #e2e8f0',
            borderRadius: '16px 16px 0 0',
            overflow: 'hidden'
          }}>
            <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0' }}>
              <button
                onClick={() => setActiveTab('submission')}
                style={{
                  flex: 1,
                  padding: '16px 24px',
                  border: 'none',
                  background: activeTab === 'submission' ? 'linear-gradient(135deg, #3f62b5 0%, #2563eb 100%)' : '#fff',
                  color: activeTab === 'submission' ? '#fff' : '#64748b',
                  fontWeight: activeTab === 'submission' ? 700 : 600,
                  fontSize: '14px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                <FiUser style={{ marginRight: '8px', display: 'inline' }} /> Submission Information
              </button>
              <button
                onClick={() => setActiveTab('content')}
                style={{
                  flex: 1,
                  padding: '16px 24px',
                  border: 'none',
                  background: activeTab === 'content' ? 'linear-gradient(135deg, #3f62b5 0%, #2563eb 100%)' : '#fff',
                  color: activeTab === 'content' ? '#fff' : '#64748b',
                  fontWeight: activeTab === 'content' ? 700 : 600,
                  fontSize: '14px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                <FiFileText style={{ marginRight: '8px', display: 'inline' }} /> Article Content
              </button>
              <button
                onClick={() => setActiveTab('social')}
                style={{
                  flex: 1,
                  padding: '16px 24px',
                  border: 'none',
                  background: activeTab === 'social' ? 'linear-gradient(135deg, #3f62b5 0%, #2563eb 100%)' : '#fff',
                  color: activeTab === 'social' ? '#fff' : '#64748b',
                  fontWeight: activeTab === 'social' ? 700 : 600,
                  fontSize: '14px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                <FiShare2 style={{ marginRight: '8px', display: 'inline' }} /> Social Media Preview
              </button>
            </div>
          </div>

            {/* Tab Content */}
            <div style={{ 
              background: '#fff', 
              borderRadius: '0 0 16px 16px',
              padding: '32px'
            }}>
              {activeTab === 'submission' && (
                <div>
                  <h2 style={{ 
                    fontSize: '20px', 
                    fontWeight: 700, 
                    color: '#1e293b', 
                    marginBottom: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <FiUser style={{ color: '#3f62b5' }} /> Submission Information
                  </h2>
                  
                  {loading ? (
                    <div className="info-grid">
                      {[...Array(4)].map((_, i) => (
                        <div className="info-item" key={i}>
                          <label>
                            <div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '120px' }} />
                          </label>
                          <div className="info-value">
                            <div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: `${60 + (i % 3) * 20}%` }} />
                            <div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: `${50 + (i % 4) * 15}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                  <div className="info-grid" style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 1fr', 
                    gap: '20px' 
                  }}>
                    <div className="info-item">
                      <label style={{ color: '#64748b', fontWeight: 600, fontSize: '14px', marginBottom: '8px' }}>
                        Submitted By
                      </label>
                      <div className="info-value" style={{ 
                        background: '#f8fafc', 
                        border: '1px solid #e2e8f0', 
                        borderRadius: '10px',
                        padding: '14px 18px',
                        textAlign: 'center'
                      }}>
                        <div className="creator-name" style={{ 
                          fontSize: '15px', 
                          fontWeight: 700, 
                          color: '#1e293b',
                          marginBottom: '4px'
                        }}>{getContentData().author}</div>
                        <div className="creator-email" style={{ 
                          fontSize: '14px', 
                          color: '#3f62b5',
                          fontWeight: 600,
                          marginBottom: '4px'
                        }}>{getContentData().authorEmail}</div>
                        <div className="creator-role" style={{ 
                          fontSize: '14px', 
                          color: '#64748b',
                          fontWeight: 600
                        }}>{getContentData().authorRole}</div>
                      </div>
                    </div>
                    
                    <div className="info-item">
                      <label style={{ color: '#64748b', fontWeight: 600, fontSize: '14px', marginBottom: '8px' }}>
                        <FiCalendar style={{ marginRight: '6px', color: '#3f62b5' }} /> Submitted Date
                      </label>
                      <div className="info-value" style={{ 
                        background: '#f8fafc', 
                        border: '1px solid #e2e8f0', 
                        borderRadius: '10px',
                        padding: '12px 16px',
                        fontSize: '15px',
                        fontWeight: 600
                      }}>{formatDate(getContentData().submittedAt)}</div>
                    </div>

                    <div className="info-item">
                      <label style={{ color: '#64748b', fontWeight: 600, fontSize: '14px', marginBottom: '8px' }}>Category</label>
                      <div className="info-value" style={{ 
                        background: '#f8fafc', 
                        border: '1px solid #e2e8f0', 
                        borderRadius: '10px',
                        padding: '12px 16px',
                        fontSize: '15px',
                        fontWeight: 600
                      }}>{getContentData().category}</div>
                    </div>

                    {getContentData().minorEligible === true && (
                      <div className="info-item">
                        <label style={{ color: '#64748b', fontWeight: 600, fontSize: '14px', marginBottom: '8px' }}>Content Rating</label>
                        <div className="info-value" style={{ 
                          background: '#f0fdf4', 
                          border: '1px solid #bbf7d0', 
                          borderRadius: '10px',
                          padding: '12px 16px'
                        }}>
                          <span className="minor-eligible-badge" style={{ 
                            color: '#059669', 
                            fontWeight: 700, 
                            fontSize: '14px' 
                          }}>Minor Eligible Content</span>
                        </div>
                      </div>
                    )}
                    
                    {getContentData().minorEligible === false && (
                      <div className="info-item">
                        <label style={{ color: '#64748b', fontWeight: 600, fontSize: '14px', marginBottom: '8px' }}>Content Rating</label>
                        <div className="info-value" style={{ 
                          background: '#fef2f2', 
                          border: '1px solid #fecaca', 
                          borderRadius: '10px',
                          padding: '12px 16px'
                        }}>
                          <span className="not-minor-eligible-badge" style={{ 
                            color: '#dc2626', 
                            fontWeight: 700, 
                            fontSize: '14px' 
                          }}>Not Minor Eligible Content</span>
                        </div>
                      </div>
                    )}

                    {getContentData().status === "Approved" && (
                      <div className="info-item">
                        <label style={{ color: '#64748b', fontWeight: 600, fontSize: '14px', marginBottom: '8px' }}>
                          <FiClock style={{ marginRight: '6px', color: '#3f62b5' }} /> Approved Date
                        </label>
                        <div className="info-value" style={{ 
                          background: '#f8fafc', 
                          border: '1px solid #e2e8f0', 
                          borderRadius: '10px',
                          padding: '12px 16px',
                          fontSize: '15px',
                          fontWeight: 600
                        }}>{formatDate(getContentData().approvedAt)}</div>
                      </div>
                    )}

                    {getContentData().status === "Rejected" && (
                      <div className="info-item">
                        <label style={{ color: '#64748b', fontWeight: 600, fontSize: '14px', marginBottom: '8px' }}>
                          <FiClock style={{ marginRight: '6px', color: '#3f62b5' }} /> Rejected Date
                        </label>
                        <div className="info-value" style={{ 
                          background: '#f8fafc', 
                          border: '1px solid #e2e8f0', 
                          borderRadius: '10px',
                          padding: '12px 16px',
                          fontSize: '15px',
                          fontWeight: 600
                        }}>{formatDate(getContentData().rejectedAt || getContentData().updatedAt)}</div>
                      </div>
                    )}

                    {getContentData().status === "Archived" && (
                      <div className="info-item">
                        <label style={{ color: '#64748b', fontWeight: 600, fontSize: '14px', marginBottom: '8px' }}>
                          <FiClock style={{ marginRight: '6px', color: '#92400e' }} /> Archived Date
                        </label>
                        <div className="info-value" style={{ 
                          background: '#f8fafc', 
                          border: '1px solid #e2e8f0', 
                          borderRadius: '10px',
                          padding: '12px 16px',
                          fontSize: '15px',
                          fontWeight: 600
                        }}>{formatDate(getContentData().archivedAt || getContentData().updatedAt || getContentData().submittedAt)}</div>
                      </div>
                    )}

                    {getContentData().status === "Rejected" && (getContentData().rejectionReason || getContentData().rejectReason) && (
                      <div className="info-item">
                        <label style={{ color: '#64748b', fontWeight: 600, fontSize: '14px', marginBottom: '8px' }}>
                          <FiXCircle style={{ marginRight: '6px', color: '#dc2626' }} /> Rejection Reason
                        </label>
                        <div className="info-value" style={{ 
                          background: '#fef2f2', 
                          border: '1px solid #fecaca', 
                          borderRadius: '10px',
                          padding: '14px 18px',
                          fontSize: '14px',
                          fontWeight: 500,
                          color: '#991b1b',
                          lineHeight: '1.5'
                        }}>{getContentData().rejectionReason || getContentData().rejectReason}</div>
                      </div>
                    )}
                  </div>
                  )}
                </div>
              )}

              {activeTab === 'content' && (
                <div>
                  <h2 style={{ 
                    fontSize: '20px', 
                    fontWeight: 700, 
                    color: '#1e293b', 
                    marginBottom: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <FiFileText style={{ color: '#3f62b5' }} /> Article Content
                  </h2>
                  
                  {loading ? (
                    <div className="article-content">
                      <div className="article-header">
                        <div className="admin-aura-skeleton admin-aura-skeleton-title" />
                        <div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '70%' }} />
                      </div>
                      <div className="article-body">
                        {[...Array(5)].map((_, i) => (
                          <div key={i} className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: `${80 + (i % 3) * 10}%`, marginBottom: '12px' }} />
                        ))}
                      </div>
                    </div>
                  ) : (
                  <div className="article-content">
                    <div className="article-header" style={{ marginBottom: '24px' }}>
                      <h1 className="article-title" style={{ 
                        fontSize: '28px', 
                        color: '#1e293b', 
                        margin: 0,
                        fontWeight: 800
                      }}>{getContentData().title}</h1>
                      {getContentData().subtitle && (
                        <h2 className="article-subtitle" style={{ 
                          fontSize: '18px', 
                          color: '#475569', 
                          margin: '12px 0 0 0',
                          fontWeight: 600
                        }}>{getContentData().subtitle}</h2>
                      )}
                    </div>

                    {/* Tags */}
                    {getContentData().tags && getContentData().tags.length > 0 && (
                      <div className="article-tags" style={{ 
                        marginBottom: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        flexWrap: 'wrap'
                      }}>
                        <FiTag style={{ color: '#3f62b5' }} />
                        {getContentData().tags.map((tag, index) => (
                          <span key={index} className="tag" style={{ 
                            background: '#eff6ff',
                            color: '#1e40af',
                            padding: '6px 12px',
                            borderRadius: '8px',
                            fontSize: '13px',
                            fontWeight: 600,
                            border: '1px solid #bfdbfe'
                          }}>{tag}</span>
                        ))}
                      </div>
                    )}

                    {/* Article Body */}
                    <div className="article-body">
                      <div 
                        className="article-content-html"
                        style={{ 
                          fontSize: '16px',
                          lineHeight: '1.8',
                          color: '#334155'
                        }}
                        dangerouslySetInnerHTML={{ __html: getContentData().bodyHtml }}
                      />
                    </div>
                  </div>
                  )}
                </div>
              )}

              {activeTab === 'social' && (
                <div>
                  <h2 style={{ 
                    fontSize: '20px', 
                    fontWeight: 700, 
                    color: '#1e293b', 
                    marginBottom: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <FiShare2 style={{ color: '#3f62b5' }} /> Social Media Preview
                  </h2>
                  {loading ? (
                    <div className="social-preview-card">
                      <div className="social-image">
                        <div className="admin-aura-skeleton admin-aura-skeleton-image" />
                      </div>
                      <div className="social-content">
                        <div className="social-title">
                          <div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '80%' }} />
                        </div>
                        <div className="social-description">
                          <div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '100%' }} />
                          <div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '90%' }} />
                        </div>
                        <div className="social-domain">
                          <div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '60px' }} />
                        </div>
                      </div>
                    </div>
                  ) : (
                  <div className="social-preview-card">
                    {getContentData().socialImageUrl && (
                      <div className="social-image">
                        <img src={getContentData().socialImageUrl} alt="Social preview" />
                      </div>
                    )}
                    <div className="social-content">
                      <div className="social-title">
                        {getContentData().socialTitle || getContentData().title}
                      </div>
                      <div className="social-description">
                        {getContentData().socialDescription || stripHtmlToText(getContentData().bodyHtml).substring(0, 160)}
                      </div>
                      <div className="social-domain">auravit</div>
                    </div>
                  </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar - Content Status, Details, and Metrics */}
          <aside className="content-review-sidebar">
            <div className="sidebar-card" style={{ 
              background: '#fff',
              borderRadius: '16px',
              padding: '0',
              overflow: 'hidden',
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              border: '1px solid #e2e8f0',
              marginBottom: '20px'
            }}>
              <div style={{ 
                background: 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)',
                padding: '18px 24px'
              }}>
                <div style={{ 
                  color: '#ffffff', 
                  margin: 0, 
                  fontSize: '16px', 
                  fontWeight: 700,
                  textAlign: 'center'
                }}>Quick Actions</div>
              </div>
              <div className="action-buttons" style={{ 
                display: "flex", 
                flexDirection: "column", 
                gap: "12px",
                padding: '24px'
              }}>
                {getContentData().status === "Pending" && (
                  <div className="status-info" style={{ 
                    padding: '16px',
                    background: '#dbeafe',
                    borderRadius: '10px',
                    border: '1px solid #bfdbfe',
                    textAlign: 'center'
                  }}>
                    <p style={{ margin: 0, color: '#1e40af', fontWeight: 700, fontSize: '13px' }}>
                      {getContentData().status}
                    </p>
                    <p className="status-message" style={{ 
                      margin: '8px 0 0 0', 
                      color: '#1e3a8a', 
                      fontSize: '13px',
                      lineHeight: '1.5'
                    }}>
                      This content is currently pending review and approval.
                    </p>
                  </div>
                )}
                
                {getContentData().status === "Approved" && (
                  <div className="status-info" style={{ 
                    padding: '16px',
                    background: '#d1fae5',
                    borderRadius: '10px',
                    border: '1px solid #a7f3d0',
                    textAlign: 'center'
                  }}>
                    <p style={{ margin: 0, color: '#065f46', fontWeight: 700, fontSize: '13px' }}>
                      {getContentData().status}
                    </p>
                    <p className="status-message" style={{ 
                      margin: '8px 0 0 0', 
                      color: '#047857', 
                      fontSize: '13px',
                      lineHeight: '1.5'
                    }}>
                      This content is now live and visible to users.
                    </p>
                  </div>
                )}
                
                {getContentData().status === "Rejected" && (
                  <div className="status-info" style={{ 
                    padding: '16px',
                    background: '#fee2e2',
                    borderRadius: '10px',
                    border: '1px solid #fecaca',
                    textAlign: 'center'
                  }}>
                    <p style={{ margin: 0, color: '#991b1b', fontWeight: 700, fontSize: '13px' }}>
                      {getContentData().status}
                    </p>
                    <p className="status-message" style={{ 
                      margin: '8px 0 0 0', 
                      color: '#b91c1c', 
                      fontSize: '13px',
                      lineHeight: '1.5'
                    }}>
                      The content was not approved and will not be published.
                    </p>
                  </div>
                )}
                
                {getContentData().status === "Archived" && (
                  <div className="status-info" style={{ 
                    padding: '16px',
                    background: '#fef3c7',
                    borderRadius: '10px',
                    border: '1px solid #fde68a',
                    textAlign: 'center'
                  }}>
                    <p style={{ margin: 0, color: '#92400e', fontWeight: 700, fontSize: '13px' }}>
                      {getContentData().status}
                    </p>
                    <p className="status-message" style={{ 
                      margin: '8px 0 0 0', 
                      color: '#b45309', 
                      fontSize: '13px',
                      lineHeight: '1.5'
                    }}>
                      This content has been archived and is no longer visible to users.
                    </p>
                  </div>
                )}
                
                {getContentData().status !== "Rejected" && getContentData().status !== "Archived" && (
                  <button 
                    onClick={() => navigate(`/admin/revise-content/${approvalId}`)}
                    style={{
                      borderRadius: "10px",
                      padding: "12px 20px",
                      border: "none",
                      background: "linear-gradient(135deg, #3f62b5 0%, #2563eb 100%)",
                      color: "white",
                      fontSize: "14px",
                      fontWeight: "700",
                      cursor: "pointer",
                      width: "100%",
                      boxShadow: "0 4px 12px rgba(63, 98, 181, 0.3)",
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    <FiEdit2 />
                    Revise Content
                  </button>
                )}
                
                {getContentData().status === "Approved" && (
                  <button 
                    onClick={handleArchiveClick}
                    disabled={isArchiving}
                    style={{
                      borderRadius: "10px",
                      padding: "12px 20px",
                      border: "none",
                      background: isArchiving ? "#9ca3af" : "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                      color: "white",
                      fontSize: "14px",
                      fontWeight: "700",
                      cursor: isArchiving ? "not-allowed" : "pointer",
                      width: "100%",
                      boxShadow: isArchiving ? "none" : "0 4px 12px rgba(245, 158, 11, 0.3)",
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      opacity: isArchiving ? 0.7 : 1
                    }}
                  >
                    {isArchiving ? 'Archiving...' : (
                      <>
                        <MdOutlineArchive />
                        Archive Content
                      </>
                    )}
                  </button>
                )}
                
                {revision && (
                  <button 
                    onClick={() => navigate(`/revision-content/${revision.contentId}`)}
                    style={{
                      borderRadius: "10px",
                      padding: "12px 20px",
                      border: "none",
                      background: "linear-gradient(135deg, #059669 0%, #10b981 100%)",
                      color: "white",
                      fontSize: "14px",
                      fontWeight: "700",
                      cursor: "pointer",
                      width: "100%",
                      boxShadow: "0 4px 12px rgba(5, 150, 105, 0.3)",
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    <FiCheck />
                    Review Proposed Revisions
                  </button>
                )}
                
                {getContentData().status === "Rejected" && (
                  <button 
                    onClick={handleDeleteClick}
                    disabled={isDeleting}
                    style={{
                      borderRadius: "10px",
                      padding: "12px 20px",
                      border: "none",
                      background: isDeleting ? "#9ca3af" : "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)",
                      color: "white",
                      fontSize: "14px",
                      fontWeight: "700",
                      cursor: isDeleting ? "not-allowed" : "pointer",
                      width: "100%",
                      boxShadow: isDeleting ? "none" : "0 4px 12px rgba(220, 38, 38, 0.3)",
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      opacity: isDeleting ? 0.7 : 1
                    }}
                  >
                    {isDeleting ? 'Deleting...' : (
                      <>
                        <FiTrash2 />
                        Delete Content
                      </>
                    )}
                  </button>
                )}
                
                {getContentData().status === "Archived" && (
                  <>
                    <button 
                      onClick={handleRestoreClick}
                      disabled={isRestoring}
                      style={{
                        borderRadius: "10px",
                        padding: "12px 20px",
                        border: "none",
                        background: isRestoring ? "#9ca3af" : "linear-gradient(135deg, #059669 0%, #10b981 100%)",
                        color: "white",
                        fontSize: "14px",
                        fontWeight: "700",
                        cursor: isRestoring ? "not-allowed" : "pointer",
                        width: "100%",
                        boxShadow: isRestoring ? "none" : "0 4px 12px rgba(5, 150, 105, 0.3)",
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        opacity: isRestoring ? 0.7 : 1
                      }}
                    >
                      {isRestoring ? 'Restoring...' : (
                        <>
                          <FiRefreshCw />
                          Restore Content
                        </>
                      )}
                    </button>
                    <button 
                      onClick={handleDeleteClick}
                      disabled={isDeleting}
                      style={{
                        borderRadius: "10px",
                        padding: "12px 20px",
                        border: "none",
                        background: isDeleting ? "#9ca3af" : "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)",
                        color: "white",
                        fontSize: "14px",
                        fontWeight: "700",
                        cursor: isDeleting ? "not-allowed" : "pointer",
                        width: "100%",
                        boxShadow: isDeleting ? "none" : "0 4px 12px rgba(220, 38, 38, 0.3)",
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        opacity: isDeleting ? 0.7 : 1
                      }}
                    >
                      {isDeleting ? 'Deleting...' : (
                        <>
                          <FiTrash2 />
                          Delete Content
                        </>
                      )}
                    </button>
                  </>
                )}
                
                {getContentData().status === "Pending" && (
                  <>
                    <button 
                      className="approve-btn" 
                      onClick={handleApprove}
                      disabled={actionLoading}
                      style={{
                        borderRadius: "10px",
                        padding: "12px 20px",
                        border: "none",
                        background: actionLoading ? "#9ca3af" : "linear-gradient(135deg, #059669 0%, #10b981 100%)",
                        color: "white",
                        fontSize: "14px",
                        fontWeight: "700",
                        cursor: actionLoading ? "not-allowed" : "pointer",
                        width: "100%",
                        boxShadow: actionLoading ? "none" : "0 4px 12px rgba(5, 150, 105, 0.3)",
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        opacity: actionLoading ? 0.7 : 1
                      }}
                    >
                      <FiCheck />
                      Approve Content
                    </button>
                    <button 
                      className="reject-btn" 
                      onClick={handleReject}
                      disabled={actionLoading}
                      style={{
                        borderRadius: "10px",
                        padding: "12px 20px",
                        border: "none",
                        background: actionLoading ? "#9ca3af" : "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)",
                        color: "white",
                        fontSize: "14px",
                        fontWeight: "700",
                        cursor: actionLoading ? "not-allowed" : "pointer",
                        width: "100%",
                        boxShadow: actionLoading ? "none" : "0 4px 12px rgba(220, 38, 38, 0.3)",
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        opacity: actionLoading ? 0.7 : 1
                      }}
                    >
                      {actionLoading ? 'Rejecting...' : (
                        <>
                          <FiXCircle />
                          Reject Content
                        </>
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="sidebar-card" style={{ 
              background: '#fff',
              borderRadius: '16px',
              padding: '0',
              overflow: 'hidden',
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              border: '1px solid #e2e8f0',
              marginBottom: '20px'
            }}>
              <div style={{ 
                background: 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)',
                padding: '18px 24px'
              }}>
                <div style={{ 
                  color: '#ffffff', 
                  margin: 0, 
                  fontSize: '16px', 
                  fontWeight: 700,
                  textAlign: 'center'
                }}>Content Details</div>
              </div>
              <div className="detail-list" style={{ padding: '20px' }}>
                <div className="detail-item" style={{ 
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '10px',
                  padding: '12px 16px',
                  marginBottom: '10px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span className="detail-label" style={{ color: '#64748b', fontWeight: 600, fontSize: '13px' }}>Word Count:</span>
                  <span className="detail-value" style={{ color: '#2563eb', fontWeight: 700, fontSize: '14px' }}>{stripHtmlToText(getContentData().bodyHtml).split(' ').length}</span>
                </div>
                <div className="detail-item" style={{ 
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '10px',
                  padding: '12px 16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span className="detail-label" style={{ color: '#64748b', fontWeight: 600, fontSize: '13px' }}>Character Count:</span>
                  <span className="detail-value" style={{ color: '#2563eb', fontWeight: 700, fontSize: '14px' }}>{stripHtmlToText(getContentData().bodyHtml).length}</span>
                </div>
              </div>
            </div>

            {/* Metrics Container - Only show for approved content */}
            {getContentData().status === "Approved" && (
              <div className="sidebar-card" style={{ 
                background: '#fff',
                borderRadius: '16px',
                padding: '0',
                overflow: 'hidden',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ 
                  background: 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)',
                  padding: '18px 24px'
                }}>
                  <div style={{ 
                    color: '#ffffff', 
                    margin: 0, 
                    fontSize: '16px', 
                    fontWeight: 700,
                    textAlign: 'center'
                  }}>Content Engagement</div>
                </div>
                <div className="metrics-info" style={{ padding: '20px' }}>
                  <div className="metric-item" style={{ 
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '10px',
                    padding: '12px 16px',
                    marginBottom: '10px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderLeft: '4px solid #3f62b5'
                  }}>
                    <div className="metric-label" style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      color: '#475569',
                      fontWeight: 600,
                      fontSize: '14px'
                    }}>
                      <FiEye style={{ color: '#3f62b5' }} /> Views
                    </div>
                    <div className="metric-value" style={{ 
                      color: '#2563eb', 
                      fontSize: '18px',
                      fontWeight: 800 
                    }}>{statsLoading ? '...' : articleStats.views}</div>
                  </div>
                  <div className="metric-item" style={{ 
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '10px',
                    padding: '12px 16px',
                    marginBottom: '10px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderLeft: '4px solid #3f62b5'
                  }}>
                    <div className="metric-label" style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      color: '#475569',
                      fontWeight: 600,
                      fontSize: '14px'
                    }}>
                      <FiShare2 style={{ color: '#3f62b5' }} /> Shares
                    </div>
                    <div className="metric-value" style={{ 
                      color: '#2563eb', 
                      fontSize: '18px',
                      fontWeight: 800 
                    }}>{statsLoading ? '...' : articleStats.shares}</div>
                  </div>
                  <div className="metric-item" style={{ 
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '10px',
                    padding: '12px 16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderLeft: '4px solid #3f62b5'
                  }}>
                    <div className="metric-label" style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      color: '#475569',
                      fontWeight: 600,
                      fontSize: '14px'
                    }}>
                      <FiBookmark style={{ color: '#3f62b5' }} /> Bookmarks
                    </div>
                    <div className="metric-value" style={{ 
                      color: '#2563eb', 
                      fontSize: '18px',
                      fontWeight: 800 
                    }}>{statsLoading ? '...' : articleStats.bookmarks}</div>
                  </div>
                </div>
              </div>
            )}
          </aside>
        </div>
      </div>

      {/* Rejection Modal */}
      {showRejectModal && (
        <>
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }} onClick={cancelReject}>
          </div>
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '12px',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
            zIndex: 1001,
            maxWidth: '500px',
            width: '90%',
            textAlign: 'center'
          }} onClick={(e) => e.stopPropagation()}>
            <button
              onClick={cancelReject}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '20px',
                color: '#64748b',
                padding: '4px'
              }}
            >
              <FiX />
            </button>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: '#fee2e2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <FiXCircle size={32} color="#dc2626" />
            </div>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '700',
              marginBottom: '12px',
              color: '#1e293b'
            }}>
              Reject Content
            </h2>
            <p style={{
              fontSize: '16px',
              color: '#64748b',
              marginBottom: '20px'
            }}>
              Please provide a reason for rejecting this content. This will be shared with the content creator.
            </p>
            <div style={{
              background: '#f9fafb',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '20px',
              border: '1px solid #e5e7eb',
              textAlign: 'center'
            }}>
              <div style={{ fontWeight: '600', marginBottom: '12px', fontSize: '16px' }}>{getContentData().title || 'Untitled Content'}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px' }}>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>
                  <strong>Creator:</strong> {getContentData().author}
                </div>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>
                  <strong>Category:</strong> {getContentData().category || 'Uncategorized'}
                </div>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>
                  <strong>Submitted Date:</strong> {formatDateModal(getContentData().submittedAt)}
                </div>
              </div>
            </div>
            <div style={{ marginTop: "16px", marginBottom: "24px" }}>
              <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "600", color: "#374151" }}>
                Rejection Reason <span style={{ color: "#dc2626" }}>*</span>
              </label>
              <input
                type="text"
                value={rejectionReason}
                onChange={handleReasonChange}
                placeholder="Enter rejection reason"
                maxLength={50}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: rejectError ? '1px solid #dc2626' : '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  height: '110px'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = rejectError ? '#dc2626' : '#d1d5db'}
              />
              {rejectError && (
                <p style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px', marginBottom: '0' }}>
                  {rejectError}
                </p>
              )}
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={cancelReject}
                disabled={actionLoading}
                style={{
                  padding: '12px 24px',
                  background: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  cursor: actionLoading ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#374151',
                  transition: 'all 0.2s ease',
                  opacity: actionLoading ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmReject}
                disabled={actionLoading}
                style={{
                  padding: '12px 24px',
                  background: '#fee2e2',
                  border: '1px solid #fee2e2',
                  borderRadius: '8px',
                  cursor: actionLoading ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#dc2626',
                  transition: 'all 0.2s ease',
                  opacity: actionLoading ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!actionLoading) {
                    e.target.style.backgroundColor = '#dc2626';
                    e.target.style.borderColor = '#dc2626';
                    e.target.style.color = 'white';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!actionLoading) {
                    e.target.style.backgroundColor = '#fee2e2';
                    e.target.style.borderColor = '#fee2e2';
                    e.target.style.color = '#dc2626';
                  }
                }}
              >
                {actionLoading ? 'Rejecting...' : <><FiXCircle style={{ marginRight: '4px' }} /> Reject</>}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Approve Confirmation Modal */}
      {showApproveModal && (
        <>
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }} onClick={cancelApprove}>
          </div>
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '12px',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
            zIndex: 1001,
            maxWidth: '500px',
            width: '90%',
            textAlign: 'center'
          }} onClick={(e) => e.stopPropagation()}>
            <button
              onClick={cancelApprove}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '20px',
                color: '#64748b',
                padding: '4px'
              }}
            >
              <FiX />
            </button>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: '#d1fae5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <FiCheck size={32} color="#059669" />
            </div>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '700',
              marginBottom: '12px',
              color: '#1e293b'
            }}>
              Approve Content
            </h2>
            <p style={{
              fontSize: '16px',
              color: '#64748b',
              marginBottom: '20px'
            }}>
              Are you sure you want to approve this content? Once approved, it will be published and visible to all users.
            </p>
            <div style={{
              background: '#f9fafb',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '24px',
              border: '1px solid #e5e7eb',
              textAlign: 'center'
            }}>
              <div style={{ fontWeight: '600', marginBottom: '12px', fontSize: '16px' }}>{getContentData().title || 'Untitled Content'}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px' }}>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>
                  <strong>Creator:</strong> {getContentData().author}
                </div>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>
                  <strong>Category:</strong> {getContentData().category || 'Uncategorized'}
                </div>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>
                  <strong>Submitted Date:</strong> {formatDateModal(getContentData().submittedAt)}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={cancelApprove}
                disabled={actionLoading}
                style={{
                  padding: '12px 24px',
                  background: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  cursor: actionLoading ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#374151',
                  transition: 'all 0.2s ease',
                  opacity: actionLoading ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmApprove}
                disabled={actionLoading}
                style={{
                  padding: '12px 24px',
                  background: '#d1fae5',
                  border: '1px solid #d1fae5',
                  borderRadius: '8px',
                  cursor: actionLoading ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#059669',
                  transition: 'all 0.2s ease',
                  opacity: actionLoading ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!actionLoading) {
                    e.target.style.backgroundColor = '#059669';
                    e.target.style.borderColor = '#059669';
                    e.target.style.color = 'white';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!actionLoading) {
                    e.target.style.backgroundColor = '#d1fae5';
                    e.target.style.borderColor = '#d1fae5';
                    e.target.style.color = '#059669';
                  }
                }}
              >
                {actionLoading ? 'Approving...' : <><FiCheck style={{ marginRight: '4px' }} /> Approve</>}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <>
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }} onClick={handleDeleteModalClose}>
          </div>
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '12px',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
            zIndex: 1001,
            maxWidth: '500px',
            width: '90%',
            textAlign: 'center'
          }} onClick={(e) => e.stopPropagation()}>
            <button
              onClick={handleDeleteModalClose}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '20px',
                color: '#64748b',
                padding: '4px'
              }}
            >
              <FiX />
            </button>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: '#fee2e2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <FiTrash2 size={32} color="#dc2626" />
            </div>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '700',
              marginBottom: '12px',
              color: '#1e293b'
            }}>
              Delete Content
            </h2>
            <p style={{
              fontSize: '16px',
              color: '#64748b',
              marginBottom: '20px'
            }}>
              Are you sure you want to delete this content? This action cannot be undone.
            </p>
            <div style={{
              background: '#f9fafb',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '24px',
              border: '1px solid #e5e7eb',
              textAlign: 'center'
            }}>
              <div style={{ fontWeight: '600', marginBottom: '12px', fontSize: '16px' }}>{getContentData().title || 'Untitled Content'}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px' }}>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>
                  <strong>Creator:</strong> {getContentData().author}
                </div>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>
                  <strong>Category:</strong> {getContentData().category || 'Uncategorized'}
                </div>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>
                  <strong>Submitted Date:</strong> {formatDateModal(getContentData().submittedAt)}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={handleDeleteModalClose}
                disabled={isDeleting}
                style={{
                  padding: '12px 24px',
                  background: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#374151',
                  transition: 'all 0.2s ease',
                  opacity: isDeleting ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={isDeleting}
                style={{
                  padding: '12px 24px',
                  background: '#fee2e2',
                  border: '1px solid #fee2e2',
                  borderRadius: '8px',
                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#dc2626',
                  transition: 'all 0.2s ease',
                  opacity: isDeleting ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isDeleting) {
                    e.target.style.backgroundColor = '#dc2626';
                    e.target.style.borderColor = '#dc2626';
                    e.target.style.color = 'white';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isDeleting) {
                    e.target.style.backgroundColor = '#fee2e2';
                    e.target.style.borderColor = '#fee2e2';
                    e.target.style.color = '#dc2626';
                  }
                }}
              >
                {isDeleting ? 'Deleting...' : <><FiTrash2 style={{ marginRight: '4px' }} /> Delete</>}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Restore Confirmation Modal */}
      {showRestoreModal && (
        <>
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }} onClick={handleRestoreModalClose}>
          </div>
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '12px',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
            zIndex: 1001,
            maxWidth: '500px',
            width: '90%',
            textAlign: 'center'
          }} onClick={(e) => e.stopPropagation()}>
            <button
              onClick={handleRestoreModalClose}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '20px',
                color: '#64748b',
                padding: '4px'
              }}
            >
              <FiX />
            </button>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: '#d1fae5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <FiRefreshCw size={32} color="#059669" />
            </div>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '700',
              marginBottom: '12px',
              color: '#1e293b'
            }}>
              Restore Content
            </h2>
            <p style={{
              fontSize: '16px',
              color: '#64748b',
              marginBottom: '20px'
            }}>
              Are you sure you want to restore this content? It will be republished and visible to all users.
            </p>
            <div style={{
              background: '#f9fafb',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '24px',
              border: '1px solid #e5e7eb',
              textAlign: 'center'
            }}>
              <div style={{ fontWeight: '600', marginBottom: '12px', fontSize: '16px' }}>{getContentData().title || 'Untitled Content'}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px' }}>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>
                  <strong>Creator:</strong> {getContentData().author}
                </div>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>
                  <strong>Category:</strong> {getContentData().category || 'Uncategorized'}
                </div>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>
                  <strong>Submitted Date:</strong> {formatDateModal(getContentData().submittedAt)}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={handleRestoreModalClose}
                disabled={isRestoring}
                style={{
                  padding: '12px 24px',
                  background: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  cursor: isRestoring ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#374151',
                  transition: 'all 0.2s ease',
                  opacity: isRestoring ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmRestore}
                disabled={isRestoring}
                style={{
                  padding: '12px 24px',
                  background: '#d1fae5',
                  border: '1px solid #d1fae5',
                  borderRadius: '8px',
                  cursor: isRestoring ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#059669',
                  transition: 'all 0.2s ease',
                  opacity: isRestoring ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isRestoring) {
                    e.target.style.backgroundColor = '#059669';
                    e.target.style.borderColor = '#059669';
                    e.target.style.color = 'white';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isRestoring) {
                    e.target.style.backgroundColor = '#d1fae5';
                    e.target.style.borderColor = '#d1fae5';
                    e.target.style.color = '#059669';
                  }
                }}
              >
                {isRestoring ? 'Restoring...' : <><FiRefreshCw style={{ marginRight: '4px' }} /> Restore</>}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Archive Confirmation Modal */}
      {showArchiveModal && (
        <>
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }} onClick={handleArchiveModalClose}>
          </div>
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '12px',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
            zIndex: 1001,
            maxWidth: '500px',
            width: '90%',
            textAlign: 'center'
          }} onClick={(e) => e.stopPropagation()}>
            <button
              onClick={handleArchiveModalClose}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '20px',
                color: '#64748b',
                padding: '4px'
              }}
            >
              <FiX />
            </button>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: '#fef3c7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <MdOutlineArchive size={32} color="#d97706" />
            </div>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '700',
              marginBottom: '12px',
              color: '#1e293b'
            }}>
              Archive Content
            </h2>
            <p style={{
              fontSize: '16px',
              color: '#64748b',
              marginBottom: '20px'
            }}>
              Are you sure you want to archive this content? It will be removed from public view but can be restored later.
            </p>
            <div style={{
              background: '#f9fafb',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '24px',
              border: '1px solid #e5e7eb',
              textAlign: 'center'
            }}>
              <div style={{ fontWeight: '600', marginBottom: '12px', fontSize: '16px' }}>{getContentData().title || 'Untitled Content'}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px' }}>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>
                  <strong>Creator:</strong> {getContentData().author}
                </div>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>
                  <strong>Category:</strong> {getContentData().category || 'Uncategorized'}
                </div>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>
                  <strong>Submitted Date:</strong> {formatDateModal(getContentData().submittedAt)}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={handleArchiveModalClose}
                disabled={isArchiving}
                style={{
                  padding: '12px 24px',
                  background: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  cursor: isArchiving ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#374151',
                  transition: 'all 0.2s ease',
                  opacity: isArchiving ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmArchive}
                disabled={isArchiving}
                style={{
                  padding: '12px 24px',
                  background: '#fef3c7',
                  border: '1px solid #fef3c7',
                  borderRadius: '8px',
                  cursor: isArchiving ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#d97706',
                  transition: 'all 0.2s ease',
                  opacity: isArchiving ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isArchiving) {
                    e.target.style.backgroundColor = '#d97706';
                    e.target.style.borderColor = '#d97706';
                    e.target.style.color = 'white';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isArchiving) {
                    e.target.style.backgroundColor = '#fef3c7';
                    e.target.style.borderColor = '#fef3c7';
                    e.target.style.color = '#d97706';
                  }
                }}
              >
                {isArchiving ? 'Archiving...' : <><MdOutlineArchive style={{ marginRight: '4px' }} /> Archive</>}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default AdminContentReview;
